import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ReactNode } from 'react';
import { Card, EmptyState, Table, Td, Th, cx } from '@/components/ui';
import { changeColorClass, formatDate, formatKrw, formatPercent, toThousands } from '@/lib/format';
import { HelpButton } from './HelpButton';
import { usePaperChartColors } from './paperChartColors';
import { SMALL_SAMPLE, orderProfiles, rankByReturn, rankClosing, winRateLabel } from './paperDisplay';
import { PROFIT_HELP } from './profitHelp';
import { RULE_CHANGES, ruleChangesOnCurve, ruleLabelAnchor } from './ruleChanges';
import type { PaperComparison, PaperProfileStats } from './paperTypes';

/**
 * 비교 탭. (18단계, 문서 8.3절)
 *
 * 🔴 이것이 18단계의 진짜 목적이다
 *   개별 프로필 탭은 이 화면을 보기 위한 준비다.
 *   "덜 먹더라도 안전한 것과 위험해도 크게 먹는 것 중 어느 쪽이 나은가" 에
 *   숫자로 답하려고 만든다.
 *
 * 🔴 어느 쪽이 이겼는지 쓰지 않는다
 *   "승" 배지도, 굵은 글씨 강조도 두지 않는다. 숫자만 나란히 놓는다.
 *   표본이 몇 건인지 모른 채 결론을 읽으면 잘못된 확신이 생긴다.
 *
 * 🔴 승률 옆에 반드시 모수가 붙는다
 *   1승 0패의 100% 와 30승 0패의 100% 는 다르다.
 *   지난 추천 화면에서 세운 규칙을 그대로 따른다.
 */

interface Props {
  data: PaperComparison;
}

export function PaperComparisonPanel({ data }: Props) {
  const profiles = orderProfiles(data.profiles);

  return (
    <div className="space-y-4">
      <ComparisonChart data={data} profiles={profiles} />
      <ComparisonTable profiles={profiles} />
      <GoalCard data={data} profiles={profiles} />
      <RoundSummaryCard data={data} />
    </div>
  );
}

/**
 * 회차 종합 분석. (2026-08-31, docs/impl/100)
 *
 * 🔴 이 글은 설정을 바꾸지 않는다 - 그 사실을 화면에 적는다
 *   AI 가 "익절을 +5% 로 올려보라" 고 써도 그것이 코드에 닿는 경로는 없다.
 *   적어두지 않으면 읽는 사람이 "시스템이 알아서 반영했겠지" 로 오해한다.
 *   실제로 99번에서 그럴듯한 제안(분할 익절)이 측정에서 전부 진 적이 있다.
 */
function RoundSummaryCard({ data }: { data: PaperComparison }) {
  const round = data.round;
  if (!round) {
    return null;
  }

  // 🔴 "진행 중이라 없다" 와 "끝났는데 아직 안 나왔다" 는 다른 말이다
  if (round.running) {
    return null;
  }

  return (
    <Card title={`${round.roundNo}회차 종합 분석`}>
      {round.summary ? (
        <>
          {/* 본문에 빈 줄로 나뉜 세 부분이 온다. 그대로 살려 보여준다 */}
          <div className="space-y-3 text-sm leading-relaxed text-fg-muted">
            {round.summary.split(/\n\s*\n/).map((para, i) => (
              <p key={i} className="whitespace-pre-wrap">
                {toThousands(para.trim())}
              </p>
            ))}
          </div>
          <p className="mt-4 rounded-lg border border-line bg-surface-muted p-3 text-xs text-fg-subtle">
            <strong>이 글은 설정을 바꾸지 않습니다.</strong> AI가 무엇을 제안하든 매매 규칙은
            그대로입니다. 제안을 실제로 반영하려면 과거 데이터로 먼저 검증해야 하고,
            그 결과 지금 방식이 더 나은 경우가 실제로 있었습니다.
          </p>
        </>
      ) : (
        <EmptyState
          title="아직 분석이 나오지 않았습니다"
          description={`${round.roundNo}회차가 끝났습니다. 매일 21:30 배치가 모든 계좌의 성적에 거래 사실(4회차부터 · 손절이 몰린 날 · 시장 환경별 손절 비율처럼 코드가 센 숫자)과 회고(3회차까지)를 더해 종합 분석을 씁니다. 가장 먼저 쓰므로 보통 회차가 끝난 날 밤에 나옵니다.`}
        />
      )}
    </Card>
  );
}

/**
 * 두 곡선을 한 축에 겹친다.
 *
 * 🔴 축이 하나다. 두 계열 모두 시드 대비 %라 같은 눈금을 쓴다
 *   금액으로 그리면 시드가 같아도 눈금이 커서 차이가 안 보인다.
 *   그리고 시드를 바꾸는 순간 과거 구간과 축이 어긋난다.
 */
function ComparisonChart({ data, profiles }: { data: PaperComparison; profiles: PaperProfileStats[] }) {
  const c = usePaperChartColors();

  /*
    🔴 제목은 "이번 회차 수익률" 이다 (2026-10-19) — 곡선은 계좌마다 이번 회차 시작 금액 대비로 그린다(스냅샷 returnPercent).
      계좌 카드의 큰 숫자가 원금 대비 누적으로 바뀌어, 여기를 "누적" 이라 부르면 같은 이름이 다른 숫자를 가리킨다
  */
  if (data.curveDates.length === 0) {
    return (
      <Card title="이번 회차 수익률">
        <EmptyState
          title="아직 그릴 곡선이 없습니다"
          description="평일 20:50 배치가 하루 한 점씩 남깁니다."
        />
      </Card>
    );
  }

  /*
    다섯 계열을 한 행으로 합친다.

    🔴 백엔드가 날짜 축을 맞춰 보낸다
      계좌마다 자기 축으로 그리면 같은 x 좌표가 다른 날을 가리켜
      선들이 거짓으로 겹친다.

    🔴 축은 합집합이다 (2026-08-28)
      저점매수가 9일 늦게 합류했다. 교집합으로 두면 세 계좌의 지난 곡선이
      통째로 사라진다. 값이 없는 날은 null 로 오고 connectNulls 가 잇는다 -
      늦게 합류한 선은 자기 시작점부터 그려진다.
  */
  const rows = data.curveDates.map((date, index) => ({
    date: date.slice(5),
    안전: profiles.find((p) => p.profile === 'SAFE')?.curve[index] ?? null,
    분할익절: profiles.find((p) => p.profile === 'MODERATE')?.curve[index] ?? null,
    하이리스크: profiles.find((p) => p.profile === 'AGGRESSIVE')?.curve[index] ?? null,
    저점매수: profiles.find((p) => p.profile === 'BAND')?.curve[index] ?? null,
    '저점매수(리스크)': profiles.find((p) => p.profile === 'BAND_AGGRESSIVE')?.curve[index] ?? null,
    '저점매수(챌린저)': profiles.find((p) => p.profile === 'BAND_CHALLENGER')?.curve[index] ?? null,
    // 🔁 국내 스윙 — 4회차부터(서버가 참여 회차에만 보낸다 · 2026-10-10)
    '스윙(6개월)': profiles.find((p) => p.profile === 'SWING_A')?.curve[index] ?? null,
    '스윙(3개월)': profiles.find((p) => p.profile === 'SWING_C')?.curve[index] ?? null,
    // 🔴 계열이 아니라 기준선이다. 아래 <Line> 주석 참조
    시장: data.marketCurve?.[index] ?? null,
  }));

  // V120 이전 구간이면 곡선이 통째로 비어 있다. 그때는 범례에서도 빼야 한다
  const hasMarket = (data.marketCurve ?? []).some((v) => v !== null);
  // 응답에 그 계좌가 있나 — 쉬는 계좌는 서버가 뺀다(2026-10-10 · 141번 10.6)
  const hasProfile = (key: string) => profiles.some((p) => p.profile === key);

  /*
    규칙이 바뀐 날에 세로 점선을 찍는다. (2026-09-12 사용자 요청, ruleChanges.ts)

    🔴 선 앞뒤는 다른 규칙으로 굴린 구간이다
      이것이 없으면 "9월 10일부터 리스크 계좌가 갑자기 잘 나간다" 를
      시장 덕인지 규칙 덕인지 가릴 수 없다.

    곡선에 있는 날짜만 남긴다 - 축이 범주형이라 없는 날은 그려지지 않는다.
    라벨이 위로 나가야 해서 선이 있을 때만 위 여백을 넓힌다. 두 변경이 며칠
    사이로 붙으면 라벨이 겹치므로 홀짝으로 높이를 어긋나게 둔다 (offset).
  */
  const ruleChanges = ruleChangesOnCurve(RULE_CHANGES, data.curveDates);
  const topMargin = ruleChanges.length > 0 ? 30 : 8;

  return (
    <Card title="이번 회차 수익률">
      <div className="h-72 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: topMargin, right: 12, bottom: 4, left: -8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={c.line} vertical={false} />
            <XAxis dataKey="date" tick={{ fill: c.axis, fontSize: 11 }} stroke={c.line} />
            <YAxis
              tick={{ fill: c.axis, fontSize: 11 }}
              stroke={c.line}
              tickFormatter={(v) => `${v}%`}
            />
            <Tooltip
              formatter={(value) => (value === null ? '—' : `${Number(value).toFixed(2)}%`)}
              contentStyle={{
                background: c.surface,
                border: `1px solid ${c.line}`,
                borderRadius: 8,
                fontSize: 12,
              }}
            />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            {/*
              0% 기준선. 손익 분기다.
              목표선(월 50만원)은 누적액이라 %축에 그릴 수 없어 아래 카드로 뺐다.
            */}
            <ReferenceLine y={0} stroke={c.goal} strokeDasharray="4 4" />
            {/*
              규칙 변경 점선. 계열이 아니라 주석이라 축 글자색(axis)이다 - 새 색을 두지 않는다.
              x 는 rows 의 date 와 같은 "MM-DD" 꼴이어야 축에서 찾는다.
              점선 간격(3 3)은 0% 기준선(4 4)·시장(2 3)과 다르게 둬 겹쳐 보이지 않게 한다.
              textAnchor 는 선이 오른쪽 절반이면 end - 가운데 정렬이면 차트 밖으로 잘린다.
            */}
            {ruleChanges.map((change, index) => (
              <ReferenceLine
                key={change.date}
                x={change.date.slice(5)}
                stroke={c.axis}
                strokeDasharray="3 3"
                label={{
                  value: change.label,
                  position: 'top',
                  offset: index % 2 === 0 ? 4 : 16,
                  textAnchor: ruleLabelAnchor(data.curveDates.indexOf(change.date), data.curveDates.length),
                  fill: c.axis,
                  fontSize: 10,
                }}
              />
            ))}
            <Line
              dataKey="안전"
              stroke={c.safe}
              strokeWidth={2}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
            {/*
              🔴 쉬는 계좌는 서버가 빼고 보낸다(4회차부터 분할익절 · 하이리스크 · 141번 10.6).
                선을 늘 그리면 값이 없는 계열이 범례에 이름만 남는다 — 응답에 있을 때만 그린다.
            */}
            {hasProfile('MODERATE') && (
              <Line
                dataKey="분할익절"
                stroke={c.moderate}
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            )}
            {hasProfile('AGGRESSIVE') && (
              <Line
                dataKey="하이리스크"
                stroke={c.aggressive}
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            )}
            <Line
              dataKey="저점매수"
              stroke={c.band}
              strokeWidth={2}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
            {/*
              🔴 색과 파선을 함께 쓴다 (2026-09-10 사용자 요청으로 색을 더했다)

                V119 때는 색 대신 파선만 썼다. 근거가 있었다 - 네 색조차
                적색약 시야에서 가장 가까운 쌍이 ΔE 13.7 로 권장선(15)을 밑돌고,
                아홉 가지 후보를 재보니 전부 그 값을 더 떨어뜨렸다(2.8~13.7).
                실선/파선은 어떤 색각에서도 100% 갈리므로 그쪽을 택했다.

                그런데 실사용에서 "두 저점매수가 같은 색이라 구분이 안 된다" 는
                피드백이 나왔다. 파선이 갈라주긴 하지만, 곡선 다섯이 겹치는
                구간에서는 선 모양을 눈으로 좇기가 어렵다.

                🔴 그래서 파선을 없애지 않고 색만 더했다
                  색을 바꾸면 정상 시야에서 즉시 갈리고,
                  파선이 남아 있으므로 색각 이상에서도 그대로 갈린다.
                  둘 중 하나를 고르는 문제가 아니었다.

                자홍(--paper-band-risk)은 보라 옆이라 "같은 계열의 변형" 이라는
                뜻은 남기면서, 주황(하이리스크)과는 색상환에서 멀어 헷갈리지 않는다.
                0% 기준선의 파선(4 4)과는 간격을 달리해 겹쳐 보이지 않게 한다.
            */}
            <Line
              dataKey="저점매수(리스크)"
              stroke={c.bandRisk}
              strokeWidth={2}
              strokeDasharray="7 3"
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
            {/*
              저점매수(챌린저) - 3회차부터 (2026-09-18).
              청록(--paper-band-challenger)은 보라·자홍과 색상환에서 멀어 세 선이 눈으로 갈린다.
              점선 간격을 또 달리해 흑백으로 인쇄해도 구분된다.
              🔴 처음엔 "2 3" 이었는데 시장 기준선과 같은 간격이었다 (2026-09-19 I-59 점검에서 발견).
                긴 선·짧은 선이 번갈아 나오는 일점쇄선으로 바꿨다 - 리스크(7 3) · 시장(2 3) 어느 쪽과도 안 겹친다.
            */}
            <Line
              dataKey="저점매수(챌린저)"
              stroke={c.bandChallenger}
              strokeWidth={2}
              strokeDasharray="8 3 2 3"
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
            {/*
              🔁 국내 스윙 둘 (2026-10-10 · 4회차부터 — 서버가 참여 회차에만 보낸다)
                색은 dataviz 검증기로 골랐다(index.css --paper-swing-* 주석). 3개월은 짧은 점선(4 2)을 더해 색만으로 가르지 않는다
                — 리스크(7 3) · 챌린저(8 3 2 3) · 시장(2 3) 과 겹치지 않는 간격
            */}
            {hasProfile('SWING_A') && (
              <Line
                dataKey="스윙(6개월)"
                stroke={c.swingA}
                strokeWidth={2}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            )}
            {hasProfile('SWING_C') && (
              <Line
                dataKey="스윙(3개월)"
                stroke={c.swingC}
                strokeWidth={2}
                strokeDasharray="4 2"
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            )}
            {/*
              🔴 시장은 계열이 아니라 기준선이다 (V120, docs/impl/102)

                계열 색을 주면 "여섯 번째 계좌" 로 읽힌다. 0% 기준선과 같은
                회색을 쓰고 선을 얇게 해 배경으로 물린다.
                여섯 계좌 뒤가 아니라 **앞에** 그려야 곡선들이 위에 얹힌다 —
                Recharts 는 나중에 선언한 것이 위로 온다.

                🔴 이 선이 있어야 순위가 뜻을 갖는다
                  다섯이 같은 시장을 지나므로 시장이 오르면 다 오른다.
                  "1등 +20%" 가 그 기간 시장이 +50% 였다면 다섯 다 진 것이다.
            */}
            {hasMarket && (
              <Line
                dataKey="시장"
                stroke={c.goal}
                strokeWidth={1.5}
                strokeDasharray="2 3"
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-2 text-xs text-fg-subtle">
        {/*
          ✏️ 9/28 I-78 2차 — "계좌마다 시작일이 달라" 는 1 · 2회차 사정이었다. 곡선은 계좌의 이번 회차 시작일부터
            그린다(PaperQueryService — startedAt 이후 스냅샷). 이번 회차엔 여섯 선이 같은 날 출발한다
        */}
        평일 20:50 기준입니다(애프터마켓 20:00 마감 뒤). 점 {data.curveDates.length}개.
        곡선은 이번 회차 시작일부터 그립니다(저점매수(챌린저)는 3회차, 스윙 두 계좌는 4회차에 합류했습니다).
      </p>
      {/* 점선이 무엇인지 적는다. 라벨만으로는 "무엇이 바뀌었나" 가 안 읽힌다 */}
      {ruleChanges.length > 0 && (
        <p className="mt-1 text-xs text-fg-subtle">
          <strong>세로 점선</strong> = 규칙이 바뀐 날:{' '}
          {ruleChanges.map((change, index) => (
            <span key={change.date}>
              {index > 0 && ' · '}
              <span className="tabular">{change.date.slice(5)}</span> {change.label} ({change.detail})
            </span>
          ))}
          . 점선 앞뒤는 다른 규칙으로 굴린 구간이라 그대로 견주면 안 됩니다.
        </p>
      )}
      <p className="mt-1 text-xs text-fg-subtle">
        {/* 리스크는 이미 자홍(--paper-band-risk)으로 색이 갈려 있었다 - "같은 보라색" 은 옛 설명 (2026-09-19 I-59) */}
        저점매수 계열은 선 모양으로도 가릅니다 — <strong>저점매수</strong>는 보라 실선,{' '}
        <strong>저점매수(리스크)</strong>는 자홍 점선, <strong>저점매수(챌린저)</strong>는 청록
        일점쇄선(긴 선 · 짧은 선이 번갈아)입니다. 스윙(6개월)은 황토색 실선, 스윙(3개월)은 연두색 짧은
        점선입니다(4회차부터). 흑백으로 봐도 구분되게 했습니다.
      </p>
      {/*
        🔴 벤치마크가 무엇인지 반드시 적는다
          "시장" 이라는 말만 보면 코스피 지수로 읽는다. 우리 것은 동일가중이라
          다르게 움직인다. 그 차이를 안 적으면 지수와 어긋날 때 버그로 보인다.
      */}
      {hasMarket ? (
        <p className="mt-1 text-xs text-fg-subtle">
          <strong>시장</strong>은 회색 점선입니다. 계좌가 아니라 <strong>기준선</strong>입니다 —
          같은 기간에 코스피 종목을 <strong>똑같은 금액씩</strong> 나눠 사서 그냥 들고 있었으면
          얼마였는지를 나타냅니다. 코스피 지수와는 다릅니다(지수는 삼성전자처럼 큰 회사가
          더 크게 반영됩니다). 우리 계좌들도 종목마다 비슷한 금액을 넣기 때문에 이렇게
          맞춰야 견줄 수 있습니다. <strong>이 선보다 아래에 있는 계좌는, 아무것도 고르지 않고
          그냥 다 샀을 때보다 못한 것입니다.</strong>
        </p>
      ) : (
        <p className="mt-1 text-xs text-fg-subtle">
          시장 기준선은 2026-08-31부터 쌓입니다. 그 전 구간은 비교할 기준선이 없습니다.
        </p>
      )}
    </Card>
  );
}

function ComparisonTable({ profiles }: { profiles: PaperProfileStats[] }) {
  return (
    <Card title="성과 비교">
      <Table>
        <thead>
          <tr>
            <Th>항목</Th>
            {profiles.map((p) => (
              <Th key={p.profile} align="right">
                {p.profileLabel}
              </Th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* 이름은 계좌 카드와 같게 "총자산"(2026-10-19 화면 점검 — 같은 값을 화면마다 다른 이름으로 불렀다) */}
          <Row label="총자산" profiles={profiles} render={(p) => formatKrw(p.total)} />
          {/* 손익·실현 손익의 ? 는 같은 글(profitHelp.ts)을 연다. 두 말의 차이가 곧 설명의 핵심이다
              ✏️ 2026-10-19 — 이 값은 이번 회차 손익이다(카드의 같은 자리 금액은 원금 대비 누적이라 이름으로 가른다) */}
          <Row
            label={
              <span className="inline-flex items-center">
                이번 회차 손익
                <HelpButton help={PROFIT_HELP} />
              </span>
            }
            profiles={profiles}
            render={(p) => (
              <span className={changeColorClass(p.profitAmount)}>{formatKrw(p.profitAmount)}</span>
            )}
          />
          {/*
            🔴 이 칸은 이번 회차 수익률이다 (2026-10-19 — 전 이름 "누적 수익률"). 밑의 작은 "누적" 이 원금 대비다.
              계좌 카드의 큰 숫자(원금 대비 누적)와 이름이 겹치지 않게 갈랐다
          */}
          <Row
            label="이번 회차 수익률"
            profiles={profiles}
            render={(p) => (
              <span>
                <span className={changeColorClass(p.totalReturnPercent)}>
                  {formatPercent(p.totalReturnPercent)}
                </span>
                <span className="block text-xs text-fg-subtle">
                  누적{' '}
                  <span className={changeColorClass(p.cumulativeReturnPercent)}>
                    {formatPercent(p.cumulativeReturnPercent)}
                  </span>
                </span>
              </span>
            )}
          />
          <Row
            label={
              <span className="inline-flex items-center">
                실현 손익
                <HelpButton help={PROFIT_HELP} />
              </span>
            }
            profiles={profiles}
            render={(p) => (
              <span className={changeColorClass(p.realizedPnl)}>{formatKrw(p.realizedPnl)}</span>
            )}
          />
          {/* 🔴 승률은 모수와 한 칸에 둔다. 떼어놓으면 큰 숫자만 읽힌다 */}
          <Row
            label="승률"
            profiles={profiles}
            render={(p) => {
              const label = winRateLabel(p);
              return (
                <span>
                  <span className="tabular">{label.value}</span>
                  <span className="ml-1 block text-xs text-fg-subtle">{label.sample}</span>
                  {label.small && (
                    <span className="block text-xs text-warn">표본이 적어 참고용입니다</span>
                  )}
                </span>
              );
            }}
          />
          <Row
            label="평균 보유일"
            profiles={profiles}
            render={(p) => (p.avgHoldingDays === null ? '—' : `${p.avgHoldingDays}일`)}
          />
          {/* 🔴 시드 대비가 아니라 고점 대비다. 실전에서 견뎌야 하는 낙폭이 이것이다 */}
          <Row
            label="최대 낙폭"
            profiles={profiles}
            render={(p) =>
              p.maxDrawdownPercent === null ? '—' : (
                <span className={changeColorClass(p.maxDrawdownPercent)}>
                  {formatPercent(p.maxDrawdownPercent)}
                </span>
              )
            }
          />
          <Row label="보유 중" profiles={profiles} render={(p) => `${p.holdingCount}건`} />
          <Row
            label="매도 사유"
            profiles={profiles}
            render={(p) => (
              <span className="text-xs">
                목표 {p.targetCount} · 손절 {p.stopLossCount} · 본전 {p.breakevenCount} · 만료 {p.expiredCount} · 교체 {p.rotatedCount} · 회차 종료 {p.roundEndCount}
              </span>
            )}
          />
          {/* 🔴 선별되면 무조건 사기로 한 뒤 생긴 항목이다 (2026-08-18) */}
          <Row
            label="진입 조건 이탈 매수"
            profiles={profiles}
            render={(p) => (p.brokenEntryCount === 0 ? '0건' : `${p.brokenEntryCount}건`)}
          />
        </tbody>
      </Table>
      <p className="mt-3 text-xs text-fg-subtle">
        어느 쪽이 나은지는 숫자만 보고 직접 판단합니다. 화면은 승패를 정하지 않습니다.
      </p>
      {/*
        🔴 세는 단위가 계좌마다 다르다는 것을 밝힌다 (2026-08-28)
          앞의 셋은 "체결 1건 = 1건" 이고 저점매수는 "종목 하나의 사다리 전체 = 1건" 이다.
          같은 열에 나란히 놓으면서 이 차이를 안 적으면 표가 조용히 오해를 만든다.
      */}
      <p className="mt-1 text-xs text-fg-subtle">
        <strong>계좌마다 세는 단위가 다릅니다.</strong> 추천 계좌는 체결 하나가 1건이고,
        저점매수 · 스윙은 한 종목의 분할 매수 전체(저점매수 · 리스크는 최대 3번, 챌린저는
        나눠 사지 않아 1번, 스윙은 최대 3단)가 묶여 1건입니다. 본전 · 만료 · 교체는 저점매수에
        없는 규칙이라 항상 0입니다. 스윙의 익절은 계단 익절을 끝까지 마친 건수이고, 스윙에는
        본전 · 만료가 있습니다. 스윙의 실현 손익은 다 판 종목만 셉니다 — 계단 익절로 일부 판
        몫은 총자산에는 들어 있고, 그 종목을 다 팔 때 실현에 들어갑니다. 회차 종료는 회차 마지막 날 한꺼번에 정리한 건수입니다 — 2회차까지만
        있고, 3회차부터는 끝나도 팔지 않고 다음 회차로 넘기므로 생기지 않습니다.
      </p>
      {/*
        🔴 두 저점매수의 차이를 표 옆에 적는다 (V119)
          같은 이름이 두 열에 나란히 있으면 "왜 둘이지" 로 읽힌다.
          다른 것이 딱 하나뿐이라는 사실이 이 비교의 전부다.
      */}
      {/*
        🔴 2026-09-10 부터 익절도 다르다 (ruleChanges.ts)
          그전까지는 "매수 기준가 하나뿐" 이 맞았다. 리스크 계좌의 익절이 +4% 로
          올라가면서 둘이 갈리는 것이 두 개가 됐다. 문구를 안 고치면 화면이 거짓말을 한다.
      */}
      {/*
        🔴 2026-09-21 부터 손절도 다르다 (ruleChanges.ts · trading.yml stop-atr-multiple 주석)
          저점매수 손절 2 → 1.5 ATR, 리스크 익절 +4% → +5%. 다른 것이 셋이 됐다.
      */}
      <p className="mt-1 text-xs text-fg-subtle">
        <strong>저점매수와 저점매수(리스크)는 매수 기준가 · 익절 폭 · 손절 폭이 다릅니다.</strong>{' '}
        저점매수는 최근 6개월 종가의 아래에서 20% 지점까지 내려와야 사고 +3%에 팔며,
        저점매수(리스크)는 40% 지점에서 사고 +5%에 팝니다(9/21부터. 9/10~9/20 +4%, 그전에는
        둘 다 +3%). 손절은 저점매수가 1.5 ATR, 리스크가 2 ATR입니다(9/21부터. 그전에는 둘 다 2 ATR).
        뒤쪽이 더 자주 사는 대신 덜 눌린 자리에서 삽니다 — 감시 종목 · 분할 매수 규칙은 같습니다.
      </p>
      {/*
        챌린저 설명 (2026-09-19 I-59) - 리스크와 다섯 가지가 다르다 (trading.yml band.challenger-*)
        ✏️ 2026-09-28 I-78 2차 — "네 가지" 였는데 9/21 리스크 익절이 +5% 가 되면서 익절폭도 갈라졌다
        ✏️ 2026-10-02 I-88 — 보유 상한 8 은 3회차까지(band.round-rules). 이 문구는 설정을 받지 못하는 사본이라
          10/9 배포 뒤 3회차 · 4회차 양쪽에서 맞도록 "3회차까지" 로 적는다
      */}
      <p className="mt-1 text-xs text-fg-subtle">
        <strong>저점매수(챌린저)</strong>는 3회차(9/21)부터 붙은 도전자입니다. 저점매수(리스크)와
        견주려고 만들었고 다섯 가지가 다릅니다(4회차부터는 넷) — 더 얕은 50% 지점에서 사고, 나눠 사지 않고
        종목당 총자산 10%를 한 번에 쓰며, 3회차까지는 동시에 8종목까지만 들고 갔고(4회차부터는 상한 없이
        예수금이 다할 때까지 — 종목당 10%라 사실상 10종목), 감시 대상 중 왕복성 상위
        30종목만 새로 사며, 익절이 +4%입니다(리스크는 9/21부터 +5%). 손절은 리스크와 같은 2 ATR입니다.
      </p>
      {/*
        🔴 2026-08-23 부터 구간 밖이면 사지 않는다 (trading.yml entry-zone-required: true)
          그 전 한 주 동안 "선별되면 무조건 산다" 였던 때의 기록이라 새로 늘지 않는다.
          전에는 지금도 무조건 사는 것처럼 적혀 있었다 (2026-09-19 I-59)
      */}
      <p className="mt-1 text-xs text-fg-subtle">
        <strong>진입 조건 이탈 매수</strong>는 선별 근거(이격도)가 사는 순간에는 깨져 있었는데도 산
        건수입니다. 2026-08-18~08-22에 "선별되면 무조건 산다" 로 굴렸던 때의 기록이고, 8/23부터는
        구간 밖이면 사지 않고 넘기므로 새로 늘지 않습니다.
      </p>
    </Card>
  );
}

/**
 * 월 목표 대비.
 *
 * 🔴 달성·실패라고 쓰지 않는다
 *   목표는 사용자가 정한 기준선이고 코드가 성패를 선언하지 않는다.
 *   차액만 보여준다.
 */
function GoalCard({ data, profiles }: { data: PaperComparison; profiles: PaperProfileStats[] }) {
  /*
    🔴 목표 달성 카드에서 성적 순위로 바꿨다 (2026-08-30 사용자 확정)

      원래는 "월 50만원(시드의 2.5%)" 을 기준선으로 두고 달성 여부를 보여줬다.
      사용자가 보고 싶은 것은 목표 달성이 아니라 "같은 조건에서 어느 세팅이
      가장 많이 벌었나" 다. 그래서 기준선을 없애고 순위를 매긴다.

      🔴 순위를 매기되 "이겼다" 고 말하지 않는다
        표본이 적으면 순위는 며칠 만에 뒤집힌다. 그래서 청산 건수를 함께 적고,
        전체 표본이 적으면 그 사실을 위에 밝힌다. (승률 표시와 같은 원칙)
  */
  const ranked = rankByReturn(profiles);
  const totalClosed = profiles.reduce((sum, p) => sum + p.closedCount, 0);
  const round = data.round;

  /*
    🔴 끝난 회차는 마감 성적으로 고정한다 (2026-09-29 L4 · 사용자 결정)
      4회차부터 회차가 끝나도 보유를 팔지 않는다(U-31). 그러면 아래 누적 수익률은 마감 뒤에도 매일 움직여
      "N회차 성적 순위" 가 회차 이력 탭의 성적표와 어긋난다. 성적표가 있으면 그 값으로 줄 세우고 제목을 바꾼다.
      성적표가 없는 끝난 회차(1~2회차 · 끝날 때 청산)는 보유가 없어 숫자가 안 움직이므로 지금 방식 그대로다
  */
  if (round && !round.running && (round.closing?.length ?? 0) > 0) {
    const closing = rankClosing(round.closing, profiles);
    return (
      <Card title={`${round.roundNo}회차 최종 성적`}>
        <p className="text-sm text-fg-muted">
          {formatDate(closing[0]?.pricedOn ?? null)} 종가로 평가한 성적표입니다 · 회차 이력 탭과 같은 값이라 더는 바뀌지 않습니다
        </p>
        <ol className="mt-3 space-y-2">
          {closing.map((c, index) => (
            <li
              key={c.account}
              className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-lg border border-line p-3"
            >
              <span className="text-sm font-semibold text-fg-subtle tabular">{index + 1}</span>
              <span className="text-sm font-medium">{c.label}</span>
              <span className={cx('text-lg tabular', changeColorClass(c.returnPercent))}>
                {formatPercent(c.returnPercent)}
              </span>
              <span className={cx('text-sm tabular', changeColorClass(c.profit))}>
                {formatKrw(c.profit)}
              </span>
              <span className="ml-auto text-xs text-fg-subtle">
                회차 시작 {formatKrw(c.startEquity)} · 마감 {formatKrw(c.endEquity)}
              </span>
            </li>
          ))}
        </ol>
        <p className="mt-3 text-xs text-fg-subtle">
          {round.roundNo}회차가 {formatDate(round.endedAt)}에 끝났습니다. 보유는 팔지 않고 다음 회차로 넘어가서, 계좌
          카드의 금액은 그 뒤에도 움직입니다 — 순위는 마감 성적으로 고정했습니다. 새 회차가 시작될 때까지 새로 사지
          않고, 들고 있는 종목의 매도 규칙(목표 · 손절 · 만료 등)만 돕니다. 화면이 승패를 선언하지 않습니다.
        </p>
      </Card>
    );
  }

  return (
    <Card title={round ? `${round.roundNo}회차 성적 순위` : '성적 순위'}>
      <p className="text-sm text-fg-muted">
        이번 회차 수익률 순입니다 · 경과 {data.elapsedMonths}개월
        {round?.plannedEndDate && ` · ${round.plannedEndDate} 종료 예정`}
        {round?.daysLeft !== null && round?.daysLeft !== undefined &&
          ` (${round.daysLeft}일 남음)`}
      </p>

      {/*
        🔴 기간이 끝나면 여섯 계좌가 모두 쉰다 (2026-08-30, docs/impl/88)
          화면이 그 사실을 말해주지 않으면 "왜 며칠째 매매가 없지" 로 읽힌다.
      */}
      {/*
        🔴 3회차 종료(2026-10-16)부터는 끝나도 팔지 않는다 (U-31 D1 · D2)
          보유는 다음 회차로 넘어가고, 공백 동안에는 새로 사지 않고 매도 규칙(목표 · 손절 · 만료 · 1차 익절 · 본전)만 돈다.
          서버 설정(paper.round.end-liquidate)을 되돌리면 옛 문구가 나온다 - 문구를 설정에서 받는 이유다.
      */}
      {round && !round.running && (
        <p className="mt-2 rounded-lg border border-line bg-surface-muted p-2 text-xs text-fg-muted">
          {round.carryHoldings ? (
            <>
              {round.roundNo}회차가 {formatDate(round.endedAt)}에 끝났습니다. 보유는 팔지 않고 다음 회차로
              넘어가며, 성적은 그날 종가로 평가한 금액으로 적었습니다(회차 이력 탭). 새 회차가 시작될 때까지
              새로 사지 않고, 들고 있는 종목의 매도 규칙(목표 · 손절 · 만료 등)만 돕니다.
            </>
          ) : (
            <>
              {round.roundNo}회차가 {formatDate(round.endedAt)}에 끝나 보유를 전량 청산했습니다.
              관리자가 새 회차를 시작할 때까지 매매가 멈춥니다.
            </>
          )}
        </p>
      )}

      {totalClosed < SMALL_SAMPLE && (
        <p className="mt-2 text-xs text-warn">
          아직 청산이 {totalClosed}건뿐입니다. 순위는 며칠 만에 뒤집힐 수 있어 참고만 하세요.
        </p>
      )}

      <ol className="mt-3 space-y-2">
        {ranked.map((p, index) => (
          <li
            key={p.profile}
            className="flex flex-wrap items-baseline gap-x-3 gap-y-1 rounded-lg border border-line p-3"
          >
            <span className="text-sm font-semibold text-fg-subtle tabular">{index + 1}</span>
            <span className="text-sm font-medium">{p.profileLabel}</span>
            <span className={cx('text-lg tabular', changeColorClass(p.totalReturnPercent))}>
              {formatPercent(p.totalReturnPercent)}
            </span>
            <span className={cx('text-sm tabular', changeColorClass(p.profitAmount))}>
              {formatKrw(p.profitAmount)}
            </span>
            {/* 원금 대비 누적은 작게만 — 계좌마다 시작한 회차가 달라 줄 세우기에 쓰지 않는다 (2026-10-19) */}
            <span className="whitespace-nowrap text-xs tabular text-fg-subtle">
              누적{' '}
              <span className={changeColorClass(p.cumulativeReturnPercent)}>
                {formatPercent(p.cumulativeReturnPercent)}
              </span>{' '}
              (원금 {formatKrw(p.principal)})
            </span>
            <span className="ml-auto text-xs text-fg-subtle">
              회차 시작 {formatKrw(p.seed)} · 청산 {p.closedCount}건
              {p.maxDrawdownPercent !== null &&
                ` · 최대 낙폭 ${formatPercent(p.maxDrawdownPercent)}`}
            </span>
          </li>
        ))}
      </ol>

      <p className="mt-3 text-xs text-fg-subtle">
        계좌마다 회차 시작 금액이 다를 수 있어 금액이 아니라 수익률로 줄 세웁니다. 작은 글씨 "누적" 은 처음
        원금 대비인데, 계좌마다 시작한 회차가 달라 기간이 다르므로 순위에 쓰지 않습니다. 어느 쪽이 나은지는
        숫자를 보고 직접 판단합니다 — 화면이 승패를 선언하지 않습니다.
      </p>
    </Card>
  );
}

function Row({
  label,
  profiles,
  render,
}: {
  /** 항목 이름. 도움말 ? 를 붙일 수 있게 문자열이 아니라 노드다 (2026-09-12) */
  label: ReactNode;
  profiles: PaperProfileStats[];
  render: (profile: PaperProfileStats) => ReactNode;
}) {
  return (
    <tr>
      <Td>{label}</Td>
      {profiles.map((p) => (
        <Td key={p.profile} align="right">
          {render(p)}
        </Td>
      ))}
    </tr>
  );
}
